import keyBy from 'lodash/keyBy';
import cloneDeep from 'lodash/cloneDeep';
import isEmpty from 'lodash/isEmpty';
import { CartLinkedEntitlementsSchema, ProductStatus } from '../../types';
import { v4 as generateUuid } from 'uuid';
import { getAppsForOms, OmsCapability, OmsPayload, CustomOrderEntitlement } from './utils';
import { OrderItemType } from '@atlassian/cssxp-types-entitlement';
import { PARTNER_DISCOUNT_TYPE } from '@css-xp/ppc-constants';
import { Currency, PatchOperation, PricingPlanType } from '@atlassian/cssxp-typescommon';
import { isFreeOffering } from './is-free-offering';
import { getPricingPlanId } from './get-pricing-plan-id';

type RequestParams = Partial<Parameters<typeof getAppsForOms>[0]> & {
  appsPayload: OmsPayload['entitlements'];
  linkedEntitlements: CartLinkedEntitlementsSchema[];
  isNonCommercialPricingEnabled: boolean;
  currentPricingEligibility?: `${PricingPlanType}`;
  currentCurrency?: `${Currency}`;
};

export function getAppsAmendmentAndPatchPayloadFromTransfer({
  appsPayload,
  selectedLinkedEntitlements,
  linkedEntitlements,
  partnerPromotions,
  omsCapability,
  offeringsAndPricingPlans,
  isNonCommercialPricingEnabled,
  currentPricingEligibility,
  currentCurrency,
}: RequestParams) {
  if (omsCapability === OmsCapability.BILL_ESTIMATE) {
    return [];
  }

  const appsKeyById = keyBy<CartLinkedEntitlementsSchema>(linkedEntitlements, 'id');
  let promotionPayload: OmsPayload['entitlements'] = [];
  let pricingPlanPayload: OmsPayload['entitlements'] = [];

  const transferOnlyAppsPayload = (appsPayload ?? []).filter((app) => {
    const isTransfer = app.orderType === OrderItemType.ACCOUNT_MODIFICATION_ORDER;
    const isHavingNoAmendment = !selectedLinkedEntitlements?.[app.entitlementId]?.productStatuses?.includes(
      ProductStatus.EDITED
    );
    return isTransfer && isHavingNoAmendment;
  });

  promotionPayload = transferOnlyAppsPayload
    .map((appPayload) => {
      const partnerPromotionId = partnerPromotions?.[appPayload.productId];
      const app = appsKeyById[appPayload.entitlementId];
      const offerings = offeringsAndPricingPlans?.[app.productId] || [];
      const selectedOfferingDetails = offerings.find(({ id: offeringId }) => offeringId === app.offeringId);
      const isSelectedOfferingFree = isFreeOffering(selectedOfferingDetails?.pricingType ?? '');
      if (!partnerPromotionId || isSelectedOfferingFree) {
        return {};
      }

      const entitlementAsAccountModificationPayload = cloneDeep(appPayload);
      // removing promotions part for amendment order
      delete entitlementAsAccountModificationPayload['addPromotions'];

      const existingPartnerPromotionIndex =
        app?.promotions?.findIndex(({ type }) => type === PARTNER_DISCOUNT_TYPE) ?? -1;

      const isRelatedToDirectCustomer = !app.isPartnerEntitlement;

      const cloudSiteId = app.cloudSiteId;
      const POELineItemArg: Record<string, string> = !isRelatedToDirectCustomer ? { POELineItem: 'true' } : {};
      // for direct customer entitlement add promotion
      // for partner entitlement having no existing partner promotion, add a promotion
      if (isRelatedToDirectCustomer || existingPartnerPromotionIndex < 0) {
        const metadataArg = cloudSiteId ? { metadata: { siteId: cloudSiteId, ...POELineItemArg } } : {};
        const addPromotions = [
          {
            promotionInstanceId: null,
            promotionDefinition: {
              promotionId: partnerPromotionId,
              promotionCode: null,
              customisedValues: null,
            },
          },
        ];
        const addPromotionsArg = { addPromotions };

        return {
          ...entitlementAsAccountModificationPayload,
          itemId: generateUuid(),
          orderType: OrderItemType.AMENDMENT_ORDER,
          ...addPromotionsArg,
          ...metadataArg,
          isImmediate: true,
        };
      } else {
        const metadataArg = cloudSiteId ? { metadata: { siteId: cloudSiteId, ...POELineItemArg } } : {};
        return {
          entitlementId: app.id,
          orderType: OrderItemType.PATCH_ENTITLEMENT_DETAILS_ORDER,
          productId: app.productId,
          ...metadataArg,
          operations: [
            {
              op: 'replace' as PatchOperation,
              path: '/subscription/promotionInstances/' + existingPartnerPromotionIndex,
              value: {
                promotionId: partnerPromotionId,
              },
            },
          ],
        };
      }
    })
    .filter((payload): payload is CustomOrderEntitlement => !isEmpty(payload));

  /**
   * Handling the case for partner to partner transfer.
   * For partner to partner transfer, we need to send the pricing plan change amendment order.
   * This is only when the price eligibility changes for the product.
   */
  pricingPlanPayload = transferOnlyAppsPayload
    .map((appPayload) => {
      const entitlementAsAccountModificationPayload = cloneDeep(appPayload);
      // removing promotions and nextBillDate part for amendment order as this is just for pricingPlan change
      delete entitlementAsAccountModificationPayload['addPromotions'];
      delete entitlementAsAccountModificationPayload['nextBillingAnchorTimestamp'];
      const app = appsKeyById[appPayload.entitlementId];
      const offerings = offeringsAndPricingPlans?.[app.productId] || [];
      const pricingPlanIdBasedOnPricingEligibilityAndOffering = (() => {
        if (currentPricingEligibility && currentCurrency) {
          const pricingPlanId = getPricingPlanId({
            pricingEligibility: currentPricingEligibility,
            currency: currentCurrency,
            offerings,
            offeringId: app.offeringId,
            defaultPricingEligibilityToCommercial: isNonCommercialPricingEnabled,
            enableRetainPricingPlan: !!isNonCommercialPricingEnabled,
            initialPricingPlanId: app.pricingPlanId,
            initialOfferingId: app.offeringId,
            pricingPlanType: app.pricingPlanType,
            entitlementPriceEligibility: app.priceEligibility,
          });
          if (pricingPlanId) return pricingPlanId;
        }
        return '';
      })();
      /**
       * Sending amendment order for pricing plan change only when the pricing plan id changes.
       * This is valid for partner entitlements.
       */
      const isPricingPlanIdChangeRequired = isNonCommercialPricingEnabled
        ? pricingPlanIdBasedOnPricingEligibilityAndOffering !== app.pricingPlanId
        : false;
      if (!isPricingPlanIdChangeRequired || !app.isPartnerEntitlement) {
        return {} as CustomOrderEntitlement;
      }
      return {
        ...entitlementAsAccountModificationPayload,
        itemId: generateUuid(),
        orderType: OrderItemType.AMENDMENT_ORDER,
        isImmediate: true,
        entitlementId: app.id,
        productId: app.productId,
      };
    })
    .filter((payload): payload is CustomOrderEntitlement => !isEmpty(payload));

  // checking for apps having amendment (promotion addition) as well
  const appsWithTransferAndAmendmentPayload = (appsPayload ?? []).filter((appPayload) => {
    const isTransfer = appPayload.orderType === OrderItemType.ACCOUNT_MODIFICATION_ORDER;
    const appWithChanges = {
      ...appPayload,
      ...(selectedLinkedEntitlements?.[appPayload.entitlementId] || {}),
    };
    const isTransferAndAmendment = isTransfer && appWithChanges.productStatuses?.includes(ProductStatus.EDITED);

    return isTransferAndAmendment === true;
  });

  // if no amendment is found, we simply return promotions payload
  if (!appsWithTransferAndAmendmentPayload.length) {
    return [...promotionPayload, ...pricingPlanPayload];
  }

  const appsAmendmentPayloadForTransfer: OmsPayload['entitlements'] =
    appsWithTransferAndAmendmentPayload.map((appPayload) => {
      const partnerPromotionId = partnerPromotions?.[appPayload.productId];
      const entitlementAsAccountModificationPayload = cloneDeep(appPayload);

      const addPromotions = entitlementAsAccountModificationPayload.addPromotions || [];

      const app = appsKeyById[appPayload.entitlementId];
      const isRelatedToDirectCustomer = !app.isPartnerEntitlement;
      const offerings = offeringsAndPricingPlans?.[app.productId] || [];
      const selectedOfferingDetails = offerings.find(({ id: offeringId }) => offeringId === app.offeringId);
      const isSelectedOfferingFree = isFreeOffering(selectedOfferingDetails?.pricingType ?? '');
      const existingPartnerPromotionIndex =
        app?.promotions?.findIndex(({ type }) => type === PARTNER_DISCOUNT_TYPE) ?? -1;
      // push the partner promotions
      // for direct customer non-free entitlement add promotion
      // for partner non-free entitlement having no existing partner promotion, add a promotion
      if (
        partnerPromotionId &&
        !isSelectedOfferingFree &&
        (isRelatedToDirectCustomer || existingPartnerPromotionIndex < 0)
      ) {
        addPromotions.push({
          promotionInstanceId: null,
          promotionDefinition: {
            promotionId: partnerPromotionId,
            promotionCode: null,
            customisedValues: null,
          },
        });
      }
      const addPromotionsArg = addPromotions.length ? { addPromotions } : {};

      return {
        ...appPayload,
        ...addPromotionsArg,
        itemId: generateUuid(),
        orderType: OrderItemType.AMENDMENT_ORDER,
        isImmediate: true,
      };
    }) ?? [];
  /**
   * Handling the case for partner to partner transfer where apart from amendment
   * we need to send
   */
  const patchEntitlementForPromotionPayload: OmsPayload['entitlements'] = appsWithTransferAndAmendmentPayload
    .filter((appPayload) => appsKeyById[appPayload.entitlementId]?.isPartnerEntitlement)
    .map((appPayload) => {
      const app = appsKeyById[appPayload.entitlementId];

      const cloudSiteId = app.cloudSiteId;
      const metadataArg = cloudSiteId ? { metadata: { siteId: cloudSiteId, POELineItem: 'true' } } : {};

      const existingPartnerPromotionIndex =
        app?.promotions?.findIndex(({ type }) => type === PARTNER_DISCOUNT_TYPE) ?? -1;

      const partnerPromotionId = partnerPromotions?.[appPayload.productId];

      if (existingPartnerPromotionIndex < 0 || !partnerPromotionId) {
        return {};
      }

      return {
        entitlementId: appPayload.entitlementId,
        orderType: OrderItemType.PATCH_ENTITLEMENT_DETAILS_ORDER,
        productId: appPayload.productId,
        ...metadataArg,
        operations: [
          {
            op: 'replace' as PatchOperation,
            path: '/subscription/promotionInstances/' + existingPartnerPromotionIndex,
            value: {
              promotionId: partnerPromotionId,
            },
          },
        ],
      };
    })
    .filter((payload): payload is CustomOrderEntitlement => !isEmpty(payload));

  return [...promotionPayload, ...appsAmendmentPayloadForTransfer, ...patchEntitlementForPromotionPayload];
}
